import {Component, DestroyRef, inject, Input, OnDestroy, OnInit} from '@angular/core';
import {FormControl, FormGroup, Validators} from '@angular/forms';
import {
  AangeverBurgerlijkeStaat,
  AangeverSamenwoonSituatie,
  BokkzClient,
  BokkzIbDataService,
  IncomeTaxEntity,
  Periode,
  Woonland
} from '@snelstart-ventures/fiscaal/domain-logic';
import {SelectFormControlItem, SharedUiFormControlsModule} from '@snelstart/ui-form-controls';
import {filter, Observable, shareReplay, take, tap} from 'rxjs';
import {SharedUiDetailsModule} from '@snelstart/ui-details';
import {SharedUiAlertModule} from '@snelstart/ui-alert';
import {CommonModule} from '@angular/common';
import {
  optionsLandcodesMetNLD,
  optionsLandcodesZonderNLD
} from '../../gedeelde-componenten/gedeelde-componenten.component';
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";

@Component({
  selector: 'snelstart-venture-fiscaal-feature-partner',
  templateUrl: './partner.component.html',
  styleUrls: ['./partner.component.scss'],
  imports: [
    SharedUiDetailsModule,
    SharedUiFormControlsModule,
    SharedUiAlertModule,
    CommonModule
  ]
})
export class PartnerComponent implements OnInit, OnDestroy {

  @Input() public id!: string;
  private readonly destroyRef = inject(DestroyRef);
  private readonly dataService = inject(BokkzIbDataService);
  private readonly bokkzClient = inject(BokkzClient);
  public partnerStaatInSnelStartRelatielijst = false;

  public optionsJaNee: SelectFormControlItem<boolean | undefined>[] = [
    {value: true, label: 'Ja'},
    {value: false, label: 'Nee, nog niet beschikbaar', disabled: true},
  ];

  public optionsLandcodesMetNld: SelectFormControlItem<string>[] = optionsLandcodesMetNLD;

  public optionsLandcodesZonderNld: SelectFormControlItem<string>[] = optionsLandcodesZonderNLD;

  public partnerPersoonlijkFg = new FormGroup({
    voorletters: new FormControl<string>('', {
      nonNullable: true,
      validators: [
        Validators.required,
        Validators.minLength(1),
        Validators.maxLength(20),
      ],
    }),
    roepnaam: new FormControl<string | null>(null, {
      nonNullable: true,
      validators: [
        Validators.minLength(1),
        Validators.maxLength(200),
      ],
    }),
    tussenvoegsels: new FormControl<string | null>(null, {
      nonNullable: true,
      validators: [
        Validators.minLength(1),
        Validators.maxLength(10),
      ],
    }),
    achternaam: new FormControl<string>('', {
      nonNullable: true,
      validators: [
        Validators.required,
        Validators.minLength(1),
        Validators.maxLength(200),
      ],
    }),
    geboortedatum: new FormControl<Date | null>(null),
    overlijdensdatum: new FormControl<Date | null>(null, {
      nonNullable: true,
      validators: [
        Validators.minLength(1),
        Validators.maxLength(200),
      ],
    }),
    bsn: new FormControl<string>('', {
      nonNullable: true,
      validators: [
        Validators.required,
        Validators.pattern(/^\d{9}$/),
      ],
    }),
    burgerlijkeStaat: new FormControl<AangeverBurgerlijkeStaat>(AangeverBurgerlijkeStaat.ALLEENSTAAND, {
      nonNullable: true,
      validators: [
        Validators.required,
      ],
    }),
    samenwoonSituatie: new FormControl<AangeverSamenwoonSituatie | null>(null),
    landCodeNationaliteit: new FormControl<string | null>(null)
  });

  public gezamenlijkeAangifteFg = new FormGroup({
    gezamenlijkeAangifte: new FormControl<boolean | null>(true)
  });

  public woonlandFg = new FormGroup({
    inwonerLandNonDiscriminatie: new FormControl<boolean | null>(false),
    woonlandVoldoetVoorKwalificatie: new FormControl<boolean | null>(false),
    belastingplichtigeVoldoetAanVoorwaardenKwalificatie: new FormControl<boolean | null>(false),
    landCodeWoonland: new FormControl<string | null>(null),
    periodeWoonachtigInWoonland: new FormGroup({
      begindatum: new FormControl<Date | undefined>(undefined),
      einddatum: new FormControl<Date | undefined>(undefined)
    })
  });

  readonly incomeTaxEntity$: Observable<IncomeTaxEntity> =
    this.dataService.incomeTaxEntity$.pipe(
      filter((data): data is IncomeTaxEntity => !!data?.sstRelationId),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({bufferSize: 1, refCount: true})
    );

  ngOnInit(): void {
    this.incomeTaxEntity$.pipe(
      take(1),
      takeUntilDestroyed(this.destroyRef),
      ).subscribe((data) => {
        if (data.aangifte?.partnerAangifte) {
          this.partnerPersoonlijkFg.patchValue(data.aangifte?.partnerAangifte?.aangever, {emitEvent: false});
          this.gezamenlijkeAangifteFg.patchValue({gezamenlijkeAangifte: data.aangifte.gezamenlijkeAangifte}, {emitEvent: false});
          if(data.aangifte.biljetSoort === 'C') {
            if (data.aangifte.buitenlandseBelastingplicht?.woonlandGegevens?.at(0)) {
              this.patchWoonlandGegevens(String(data.year), data);
            }
            if (data.aangifte.partnerAangifte?.buitenlandseBelastingplicht?.woonlandGegevens?.at(0)) {
              this.patchWoonlandGegevens(String(data.year), data);
            }
          }
        }
      });
    this.subscribeToBsnChanges();
    this.partnerPersoonlijkFg.valueChanges.pipe(
      tap(() => this.saveForm())
    ).subscribe();
    this.woonlandFg.valueChanges.pipe(
      tap(() => this.saveForm())
    ).subscribe();
    this.gezamenlijkeAangifteFg.valueChanges.pipe(
      tap(() => this.saveForm())
    ).subscribe();
  }

  ngOnDestroy(): void {
    // rely on saveFrom in component
  }

  private subscribeToBsnChanges() {
    this.partnerPersoonlijkFg.controls.bsn.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(bsn => {
        const year = new Date().getFullYear().toString();
        this.getPartnerEntity(bsn, year);
      });
  }

  public patchWoonlandGegevens(year: string, incomeTaxEntity: IncomeTaxEntity) {
    const buitenlandseBelastingplicht = incomeTaxEntity.aangifte?.partnerAangifte?.buitenlandseBelastingplicht;
    const woonland = buitenlandseBelastingplicht?.woonlandGegevens[0] as Woonland;

    const woonland1 = {
      landCodeWoonland: woonland.landCodeWoonland ?? '', // ensure string, never null
      periodeWoonachtigInWoonland: {
        begindatum: new Date(`${year}-01-01`),
        einddatum: new Date(`${year}-12-31`)
      } as Periode
    } as Woonland;

    this.woonlandFg.patchValue(woonland1);

    const flags = this.berekenWoonlandFlags(woonland1.landCodeWoonland);
    this.woonlandFg.patchValue(flags);

    if (buitenlandseBelastingplicht?.belastingplichtigeVoldoetAanVoorwaardenKwalificatie !== undefined) {
      this.woonlandFg.patchValue({
        belastingplichtigeVoldoetAanVoorwaardenKwalificatie: buitenlandseBelastingplicht.belastingplichtigeVoldoetAanVoorwaardenKwalificatie
      });
    }
  }

  private berekenWoonlandFlags(landCode: string): {
    inwonerLandNonDiscriminatie: boolean;
    woonlandVoldoetVoorKwalificatie: boolean
  } {
    return {
      inwonerLandNonDiscriminatie: ['BEL', 'SUR', 'AWB'].includes(landCode),
      woonlandVoldoetVoorKwalificatie: ['ALA', 'ABW', 'BEL', 'BES', 'BGR', 'CYP', 'PRT', 'DNK', 'DEU', 'EST', 'FIN',
        'FRA', 'GUF', 'GRC', 'GLP', 'HUN', 'IRL', 'ITA', 'HRV', 'LVA', 'LTU', 'LIE',
        'LUX', 'MLT', 'MTQ', 'MYT', 'NOR', 'AUT', 'POL', 'PRT', 'REU', 'ROU', 'MAF',
        'SVN', 'SVK', 'ESP', 'SUR', 'CZE', 'SWE', 'CHE'].includes(landCode)
    };
  }

  public saveForm() {
    if (this.partnerPersoonlijkFg.invalid) {
      this.partnerPersoonlijkFg.markAllAsTouched();
    }

    if (this.woonlandFg.invalid) {
      this.woonlandFg.markAllAsTouched();
    }

    this.partnerPersoonlijkFg.updateValueAndValidity({emitEvent: false});
    this.gezamenlijkeAangifteFg.updateValueAndValidity({emitEvent: false});
    this.woonlandFg.updateValueAndValidity({emitEvent: false});

    const partner = this.partnerPersoonlijkFg.getRawValue();
    partner.tussenvoegsels = partner.tussenvoegsels?.trim() ? partner.tussenvoegsels : null;
    const woonland = this.woonlandFg.getRawValue();
    const woonlandForSave = {
      ...woonland,
      landCodeWoonland: woonland.landCodeWoonland ?? '',
      periodeWoonachtigInWoonland: {
        begindatum: woonland.periodeWoonachtigInWoonland?.begindatum ?? undefined,
        einddatum: woonland.periodeWoonachtigInWoonland?.einddatum ?? undefined
      }
    };

    this.dataService.patchIncomeTaxEntity(currentEntity => {
      if (!currentEntity.aangifte) {
        return currentEntity;
      }
      const partnerAangifte = currentEntity.aangifte.partnerAangifte;
      const biljetSoort = partnerAangifte?.biljetSoort ?? 'P';
      const updatedPartnerAangifte: any = {
        ...partnerAangifte,
        biljetSoort,
        aangifteJaar: partnerAangifte?.aangifteJaar ?? currentEntity.year,
        aangever: {
          ...partnerAangifte?.aangever,
          roepnaam: partner.roepnaam ?? undefined,
          voorletters: partner.voorletters ?? undefined,
          tussenvoegsels: partner.tussenvoegsels ?? undefined,
          achternaam: partner.achternaam,
          geboortedatum: partner.geboortedatum,
          overlijdensdatum: partner.overlijdensdatum ?? undefined,
          bsn: partner.bsn ?? undefined,
          burgerlijkeStaat: currentEntity.aangifte.aangever.burgerlijkeStaat ?? undefined,
          samenwoonSituatie: currentEntity.aangifte.aangever.samenwoonSituatie ?? undefined,
          landCodeNationaliteit: partner.landCodeNationaliteit ?? undefined,
          relatiegegevens: currentEntity.aangifte.aangever.relatiegegevens ?? undefined,
        },
        gezamenlijkeAangifte: this.gezamenlijkeAangifteFg.getRawValue().gezamenlijkeAangifte ?? undefined,
      };
      if (biljetSoort === 'C') {
        updatedPartnerAangifte.buitenlandseBelastingplicht = {
          ...partnerAangifte?.buitenlandseBelastingplicht,
          inwonerLandNonDiscriminatie: this.berekenWoonlandFlags(woonlandForSave.landCodeWoonland).inwonerLandNonDiscriminatie,
          woonlandVoldoetVoorKwalificatie: this.berekenWoonlandFlags(woonlandForSave.landCodeWoonland).woonlandVoldoetVoorKwalificatie,
          belastingplichtigeVoldoetAanVoorwaardenKwalificatie: woonlandForSave.belastingplichtigeVoldoetAanVoorwaardenKwalificatie ?? false,
          woonlandGegevens: [woonlandForSave]
        };
      } else {
        delete updatedPartnerAangifte.buitenlandseBelastingplicht;
      }
      return {
        ...currentEntity,
        aangifte: {
          ...currentEntity.aangifte,
          gezamenlijkeAangifte: this.gezamenlijkeAangifteFg.getRawValue().gezamenlijkeAangifte ?? undefined,
          partnerAangifte: updatedPartnerAangifte
        }
      } as IncomeTaxEntity;
    });
  }

  public getPartnerEntity(bsn: string, year: string) {
    if (bsn && /^\d{9}$/.test(bsn)) {
      this.bokkzClient.getIncomeTax(bsn, year).subscribe(value => {
        this.partnerStaatInSnelStartRelatielijst = value.sstRelationId != undefined;
      });
    }
  }
}
